import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useEventStore } from '../../../store/eventStore';
import { useBusStore } from '../../../store/busStore';
import { XMarkIcon, TicketIcon, ArrowRightIcon, ArrowLeftIcon, CalendarIcon, ClockIcon, MapPinIcon, TruckIcon, CheckIcon, PlusIcon, MinusIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

// SMS 상세 모달 컴포넌트
const SMSDetailModel = ({ isOpen, onClose, eventData }) => {
  // 이벤트 스토어에서 필요한 함수들 가져오기
  const { getSmsEventDetail, getTicketSchedules, getSchedules, sendSms, isLoading } = useEventStore();
  const { listServices: listBusServices, getSeats: getBusSeats } = useBusStore();

  // 상태 관리
  const [eventDetail, setEventDetail] = useState(null);
  const [ticketSchedules, setTicketSchedules] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkMeetingTime, setBulkMeetingTime] = useState('09:00');
  const [bulkScheduleName, setBulkScheduleName] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  // 버스 배정 정보: { userId: [{ bus_code, seat_code, car_number }] }
  const [busAssignmentMap, setBusAssignmentMap] = useState({});
  const [hasBusServices, setHasBusServices] = useState(false);
  
  // 별도 발송 모달 관련 상태
  const [isMultiPhoneModalOpen, setIsMultiPhoneModalOpen] = useState(false);
  const [currentSmsIndex, setCurrentSmsIndex] = useState(null);
  const [currentSmsRecipient, setCurrentSmsRecipient] = useState('');
  const [multiPhoneInputs, setMultiPhoneInputs] = useState(['']);
  
  // 로딩 인디케이터 상태
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  
  // 팀별 티켓 정보 처리
  const processedTicketData = useMemo(() => {
    if (!ticketSchedules || ticketSchedules.length === 0) return {};
    
    // 팀별로 데이터 그룹화
    const teamData = {};
    
    ticketSchedules.forEach(ticket => {
      const teamId = ticket.team_id;
      if (!teamData[teamId]) {
        teamData[teamId] = {
          teamName: ticket.team_name || `팀 ${teamId}`,
          outbound: [],
          inbound: []
        };
      }
      
      // 왕편/복편 구분하여 저장
      if (ticket.direction === 'outbound' || ticket.train_type === '왕편') {
        teamData[teamId].outbound.push(ticket);
      } else if (ticket.direction === 'inbound' || ticket.train_type === '복편') {
        teamData[teamId].inbound.push(ticket);
      }
    });
    
    return teamData;
  }, [ticketSchedules]);

  // 모달이 열릴 때 데이터 로드
  useEffect(() => {
    if (isOpen && eventData) {
      // 데이터 초기화
      setEventDetail(null);
      setTicketSchedules([]);
      setSchedules([]);
      setDataList([]);
      setError(null);
      setLoading(true);
      setBusAssignmentMap({});
      setHasBusServices(false);
      
      // API 호출 로직
      const fetchData = async () => {
        try {
          // 1. 행사 상세 정보 가져오기
          let sms_log_id = null
          if (eventData.smsLogs && eventData.smsLogs.length > 0) {
            sms_log_id = eventData.smsLogs[0][0];
          }

          const eventDetailResponse = await getSmsEventDetail(
            eventData.EV_YM,
            eventData.EV_SEQ,
            sms_log_id
          );
          if (eventDetailResponse.success) {
            // 행사 상세 정보 설정
            setEventDetail(eventDetailResponse.data);

            // 버스 배정 정보 조회
            const busMap = {};
            let busExists = false;
            try {
              const busResult = await listBusServices({ ev_ym: eventData.EV_YM, ev_seq: eventData.EV_SEQ });
              if (busResult.success && busResult.data && busResult.data.length > 0) {
                busExists = true;
                // 각 버스 서비스의 좌석 정보 조회
                for (const service of busResult.data) {
                  const seatsResult = await getBusSeats(service.bus_code);
                  if (seatsResult.success && seatsResult.data) {
                    const seats = seatsResult.data.items || seatsResult.data || [];
                    seats.forEach(seat => {
                      if (seat.assigned_user_id) {
                        if (!busMap[seat.assigned_user_id]) {
                          busMap[seat.assigned_user_id] = [];
                        }
                        busMap[seat.assigned_user_id].push({
                          bus_code: service.bus_code,
                          seat_code: seat.seat_code || seat.seat_number,
                          car_number: service.car_number || '1',
                          route: service.route || '',
                        });
                      }
                    });
                  }
                }
              }
            } catch (busErr) {
              console.error('버스 배정 정보 조회 중 오류:', busErr);
            }
            setBusAssignmentMap(busMap);
            setHasBusServices(busExists);

            // 일정 정보 가져오기
            const schedulesResponse = await getSchedules(eventData.EV_YM, eventData.EV_SEQ);
            if (schedulesResponse.success) {
              setSchedules(schedulesResponse.data);
              
              // ASP 코드와 같이 nameList 생성
              const nameList = schedulesResponse.data.map(item => item.name);
              
              // 각 예약자별 데이터 처리
              const processedDataList = [];
              
              for (let detail of eventDetailResponse.data) {
                // 티켓 일정 정보 가져오기
                const RES_DAY = detail.RES_DAY;
                const RES_SEQ = detail.RES_SEQ;
                const EV_YM = eventData.EV_YM;
                const EV_SEQ = eventData.EV_SEQ;
                const userId = RES_DAY + '-' + RES_SEQ + '.' + EV_YM + '-' + EV_SEQ;
                
                // 기본 데이터 객체 생성
                const dataItem = {
                  ...detail,
                  nameList,
                  schedule_name: nameList.length > 0 ? nameList[0] : '',
                  meeting: '09:00', // 기본 미팅 시간
                  status: 'No Ticket', // 기본 상태
                  EV_YM,
                  EV_SEQ
                };
                
                // 티켓 정보 가져오기
                const ticketResponse = await getTicketSchedules(userId);
                
                if (ticketResponse.success && ticketResponse.data && ticketResponse.data.data.tickets) {
                  const responseData = ticketResponse.data.data;
          
                  // 티켓이 없는 경우
                  if (responseData.tickets.length === 0) {
                    dataItem.seatSetStatus = 'No Ticket';
                    dataItem.outbound = '배정 전';
                    dataItem.outBoundDeptTime = '';
                    dataItem.outBoundArrvTime = '';
                    dataItem.outBoundContent = '';
                    dataItem.inbound = '배정 전';
                    dataItem.inBoundDeptTime = '';
                    dataItem.inBoundArrvTime = '';
                    dataItem.inBoundContent = '';
                  } else {
                    // trainCode에서 숫자 추출 (예: "KTX 101" → 101, "202" → 202)
                    const extractTrainNumber = (code) => {
                      const match = String(code).match(/(\d+)\s*$/);
                      return match ? parseInt(match[1]) : NaN;
                    };
                    // trainCode가 홀수인 첫 번째 티켓을 왕편으로 설정
                    const outbound = responseData.tickets.find(ticket => extractTrainNumber(ticket.train_code) % 2 === 1);
                    
                    if (!outbound) {
                      dataItem.seatSetStatus = 'No Outbound Ticket';
                      dataItem.outbound = '';
                      dataItem.outBoundDeptTime = '';
                      dataItem.outBoundArrvTime = '';
                      dataItem.outBoundContent = '';
                      dataItem.outBoundTrainCode = '';
                    } else {
                      const outBoundDeptTime = outbound.departure_time.substring(11, 16);
                      const outBoundArrvTime = outbound.arrival_time.substring(11, 16);

                      dataItem.outbound = `${outBoundDeptTime} → ${outBoundArrvTime}`;
                      dataItem.outBoundDeptTime = outBoundDeptTime;
                      dataItem.outBoundArrvTime = outBoundArrvTime;
                      dataItem.outBoundContent = outbound.departure_station + '역 → ' + outbound.arrival_station + '역';
                      dataItem.outBoundTrainCode = outbound.train_code;
                    }

                    // trainCode가 짝수인 첫 번째 티켓을 복편으로 설정
                    const inbound = responseData.tickets.find(ticket => extractTrainNumber(ticket.train_code) % 2 === 0);

                    if (!inbound) {
                      dataItem.seatSetStatus = 'No Inbound Ticket';
                      dataItem.inbound = '';
                      dataItem.inBoundDeptTime = '';
                      dataItem.inBoundArrvTime = '';
                      dataItem.inBoundContent = '';
                      dataItem.inBoundTrainCode = '';
                    } else {
                      const inBoundDeptTime = inbound.departure_time.substring(11, 16);
                      const inBoundArrvTime = inbound.arrival_time.substring(11, 16);

                      dataItem.seatSetStatus = 'Success';
                      dataItem.inbound = `${inBoundDeptTime} → ${inBoundArrvTime}`;
                      dataItem.inBoundDeptTime = inBoundDeptTime;
                      dataItem.inBoundArrvTime = inBoundArrvTime;
                      dataItem.inBoundContent = inbound.departure_station + '역 → ' + inbound.arrival_station + '역';
                      dataItem.inBoundTrainCode = inbound.train_code;
                    }
                  }
                  
                  // 티켓 원본 데이터도 저장
                  dataItem.ticketData = responseData;
                }
                
                processedDataList.push(dataItem);
              }
              
              // 처리된 데이터 목록 설정
              setDataList(processedDataList);
            }
          } else {
            setError('행사 정보를 가져오는데 실패했습니다.');
          }
        } catch (err) {
          console.error('데이터 로드 중 오류 발생:', err);
          setError(err.message || '데이터를 가져오는 중 오류가 발생했습니다.');
        }
      };
      
      fetchData();
      setLoading(false);
    }


  }, [isOpen, eventData, getSmsEventDetail, getSchedules, getTicketSchedules]);

  // 별도 발송 모달 열기
  const handleOpenMultiPhoneModal = (index, phone) => {
    setCurrentSmsIndex(index);
    setCurrentSmsRecipient(phone);
    setMultiPhoneInputs(['']);
    setIsMultiPhoneModalOpen(true);
  };
  
  // 별도 발송 모달 닫기
  const handleCloseMultiPhoneModal = () => {
    setIsMultiPhoneModalOpen(false);
    setCurrentSmsIndex(null);
    setCurrentSmsRecipient('');
    setMultiPhoneInputs(['']);
  };
  
  // 전체 행 선택/해제 처리
  const handleSelectAllRows = () => {
    if (selectAll) {
      // 전체 선택 해제
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      // 전체 선택
      const allRowIndexes = dataList.map((_, index) => index);
      setSelectedRows(allRowIndexes);
      setSelectAll(true);
    }
  };
  
  // 행 선택/해제 처리
  const handleRowSelection = (index) => {
    setSelectedRows(prevSelected => {
      if (prevSelected.includes(index)) {
        // 선택 해제
        const newSelected = prevSelected.filter(i => i !== index);
        setSelectAll(false);
        return newSelected;
      } else {
        // 선택 추가
        const newSelected = [...prevSelected, index];
        if (newSelected.length === dataList.length) {
          setSelectAll(true);
        }
        return newSelected;
      }
    });
  };
  
  // 일괄 미팅 시간 변경 처리
  const handleBulkMeetingTimeChange = (time) => {
    if (!time) return;
    
    // 선택된 행들만 업데이트하거나 선택된 행이 없으면 모든 행 업데이트
    const rowsToUpdate = selectedRows.length > 0 ? selectedRows : dataList.map((_, idx) => idx);
    
    // 새로운 eventDetail 상태 생성
    const newEventDetail = [...eventDetail];
    
    // 새로운 dataList 상태 생성
    const newDataList = [...dataList];
    
    // 선택된 행들에 대해 업데이트 적용
    rowsToUpdate.forEach(index => {
      // eventDetail 업데이트
      newEventDetail[index] = {
        ...newEventDetail[index],
        meetingTime: time,
        meeting: time // UI에 표시되는 필드명
      };
      
      // dataList 업데이트
      newDataList[index] = {
        ...newDataList[index],
        meetingTime: time,
        meeting: time // UI에 표시되는 필드명
      };
    });
    
    // 상태 한 번만 업데이트
    setEventDetail(newEventDetail);
    setDataList(newDataList);
  };
  
  // 일괄 일정명 변경 처리
  const handleBulkScheduleNameChange = (name) => {
    if (!name) return;
    
    // 선택된 행들만 업데이트하거나 선택된 행이 없으면 모든 행 업데이트
    const rowsToUpdate = selectedRows.length > 0 ? selectedRows : dataList.map((_, idx) => idx);
    
    // 새로운 eventDetail 상태 생성
    const newEventDetail = [...eventDetail];
    
    // 새로운 dataList 상태 생성
    const newDataList = [...dataList];
    
    // 선택된 행들에 대해 업데이트 적용
    rowsToUpdate.forEach(index => {
      // eventDetail 업데이트
      newEventDetail[index] = {
        ...newEventDetail[index],
        scheduleName: name,
        schedule_name: name // UI에 표시되는 필드명
      };
      
      // dataList 업데이트
      newDataList[index] = {
        ...newDataList[index],
        scheduleName: name,
        schedule_name: name // UI에 표시되는 필드명
      };
    });
    
    // 상태 한 번만 업데이트
    setEventDetail(newEventDetail);
    setDataList(newDataList);
  };
  
  // 일괄 발송 처리
  const handleBulkSendSMS = async () => {
    if (selectedRows.length === 0) {
      alert('발송할 항목을 선택해주세요.');
      return;
    }
    
    const confirmMessage = `선택한 ${selectedRows.length}개 항목에 대해 SMS를 일괄 발송하시겠습니까?`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setIsLoadingAction(true);
      setShowLoadingIndicator(true);
      
      let successCount = 0;
      let failCount = 0;
      
      // 선택된 행들에 대해 순차적으로 발송
      for (const index of selectedRows) {
        const item = dataList[index];
        const phone = `${item.HANDPHONE1}${item.HANDPHONE2}${item.HANDPHONE3}`;
        
        try {
          // 개별 발송 함수 호출
          const result = await handleSendSMS(index, phone, true);

        //   if (result) {
        //     successCount++;
        //   } else {
        //     failCount++;
        //   }

        successCount++;
        
        } catch (err) {
          console.error(`항목 ${index} 발송 오류:`, err);
          failCount++;
        }
      }
      
      alert(`일괄 발송 결과: 성공 ${successCount}건, 실패 ${failCount}건`);
      
    } catch (err) {
      console.error('일괄 발송 중 오류 발생:', err);
      alert(`일괄 발송 오류: ${err.message || '알 수 없는 오류가 발생했습니다.'}`);
    } finally {
      setIsLoadingAction(false);
      setShowLoadingIndicator(false);
    }
  };
  
  // 전화번호 입력 필드 추가
  const handleAddPhoneInput = () => {
    setMultiPhoneInputs([...multiPhoneInputs, '']);
  };
  
  // 전화번호 입력 필드 삭제
  const handleRemovePhoneInput = (index) => {
    if (multiPhoneInputs.length <= 1) return;
    const newInputs = [...multiPhoneInputs];
    newInputs.splice(index, 1);
    setMultiPhoneInputs(newInputs);
  };
  
  // 전화번호 입력 값 변경
  const handlePhoneInputChange = (index, value) => {
    // 기존 배열을 복사하고 특정 인덱스의 값만 변경
    setMultiPhoneInputs(prev => {
      const newInputs = [...prev];
      newInputs[index] = value;
      return newInputs;
    });
  };
  
  // 별도 발송 모달에서 SMS 발송
  const handleSendMultiPhoneSMS = async () => {
    // 빈 입력 필드 제거
    const validPhones = multiPhoneInputs.filter(phone => phone.trim() !== '');
    
    if (validPhones.length === 0) {
      alert('최소한 하나의 전화번호를 입력해주세요.');
      return;
    }
    
    // 전화번호 형식 확인
    const phoneRegex = /^\d{10,11}$/;
    const invalidPhones = validPhones.filter(phone => !phoneRegex.test(phone.replace(/\D/g, '')));
    
    if (invalidPhones.length > 0) {
      alert(`유효하지 않은 전화번호 형식이 있습니다:\n${invalidPhones.join('\n')}`);
      return;
    }
    
    // 발송 확인
    if (!window.confirm(`${validPhones.length}개의 번호로 문자를 발송하시겠습니까?`)) {
      return;
    }
    
    setIsLoadingAction(true);
    setShowLoadingIndicator(true);
    
    try {
      // 각 번호로 발송 시도
      const results = [];
      for (const phone of validPhones) {
        await handleSendSMS(currentSmsIndex, phone);
        results.push(phone);
      }
      
      handleCloseMultiPhoneModal();
      alert(`${results.length}개의 번호로 문자 발송이 완료되었습니다.`);
    } catch (err) {
      console.error('다중 발송 중 오류 발생:', err);
      alert(`다중 발송 오류: ${err.message || '알 수 없는 오류가 발생했습니다.'}`);
    } finally {
      setIsLoadingAction(false);
      setShowLoadingIndicator(false);
    }
  };
  
  // SMS 발송 함수
  const handleSendSMS = async (index, phone, isBulkSend = false) => {
    try {
      setIsLoadingAction(true);
      setShowLoadingIndicator(true);
      
      const selectedData = dataList[index];
      
      // 티켓 배정 상태 확인
      if (selectedData.seatSetStatus === 'No Ticket') {
        if (!window.confirm(`발송번호: ${phone}\n등록된 티켓 정보가 없습니다.\n그래도 문자를 발송하시겠습니까?`)) {
          setIsLoadingAction(false);
          setShowLoadingIndicator(false);
          return;
        }
      } else if (selectedData.seatSetStatus === 'No Outbound Ticket') {
        if (!window.confirm(`발송번호: ${phone}\n왕편 티켓 정보가 없습니다.\n그래도 문자를 발송하시겠습니까?`)) {
          setIsLoadingAction(false);
          setShowLoadingIndicator(false);
          return;
        }
      } else if (selectedData.seatSetStatus === 'No Inbound Ticket') {
        if (!window.confirm(`발송번호: ${phone}\n복편 티켓 정보가 없습니다.\n그래도 문자를 발송하시겠습니까?`)) {
          setIsLoadingAction(false);
          setShowLoadingIndicator(false);
          return;
        }
      }
      
      // 일정명에 해당하는 일정 이미지 및 가이드 정보 가져오기
      const schedule = schedules.find(item => item.name === selectedData.schedule_name);
      
      if (!schedule) {
        alert('일정 정보를 찾을 수 없습니다.');
        setIsLoadingAction(false);
        setShowLoadingIndicator(false);
        return;
      }
      
      // 전화번호에서 숫자만 추출
      const cleanPhone = phone.replace(/\D/g, '');
      
      // SMS 발송을 위한 데이터 구성
      const smsData = {
        EV_YM: eventData.EV_YM,
        EV_SEQ: eventData.EV_SEQ,
        RES_DAY: selectedData.RES_DAY,
        RES_SEQ: selectedData.RES_SEQ,
        meeting: selectedData.meeting,
        outBoundDeptTime: selectedData.outBoundDeptTime,
        outBoundArrvTime: selectedData.outBoundArrvTime,
        outBoundContent: selectedData.outBoundContent,
        outBoundNote: eventDetail[index].out_bound_note || '',
        inBoundDeptTime: selectedData.inBoundDeptTime,
        inBoundArrvTime: selectedData.inBoundArrvTime,
        inBoundContent: selectedData.inBoundContent,
        inBoundNote: eventDetail[index].in_bound_note || '',
        name: selectedData.schedule_name,
        scheduleImageUrl: schedule.s3_url,
        guideName: schedule.guide_name,
        guidePhone: schedule.guide_phone,
        guideType: schedule.guide_type,
        meetingPlace: schedule.meeting_place,
        phone: cleanPhone
      };
      
      // SMS 발송 API 호출
      const response = await sendSms(smsData);
      
      if (response.success) {
        // 발송 성공 시 상태 업데이트
        const newDataList = [...dataList];
        newDataList[index].status = 'Success';
        setDataList(newDataList);

        const newEventDetail = [...eventDetail];
        newEventDetail[index] = { ...newEventDetail[index], status: 'Success' };
        setEventDetail(newEventDetail);

        if (!isBulkSend) {
          alert(`문자 발송 성공\n발송번호: ${phone}\n대표자명: ${selectedData.RES_NM}`);
          return true;
        }
      } else {
        // 발송 실패 시 상태 업데이트
        const newDataList = [...dataList];
        newDataList[index].status = 'Fail';
        setDataList(newDataList);

        const newEventDetail = [...eventDetail];
        newEventDetail[index] = { ...newEventDetail[index], status: 'Fail' };
        setEventDetail(newEventDetail);
        
        if (!isBulkSend) {
          alert(`문자 발송 실패\n발송번호: ${phone}\n대표자명: ${selectedData.RES_NM}\n${response.error || ''}`);
          return false;
        }
      }
    } catch (err) {
      console.error('SMS 발송 중 오류 발생:', err);
      alert(`문자 발송 오류: ${err.message || '알 수 없는 오류가 발생했습니다.'}`);
      return false;
    } finally {
      setIsLoadingAction(false);
      setShowLoadingIndicator(false);
    }
  };
  
  // 별도 발송 모달 컴포넌트
  const MultiPhoneModal = () => {
    if (!isMultiPhoneModalOpen) return null;
    
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-black bg-opacity-50"
           onClick={(e) => e.target === e.currentTarget && handleCloseMultiPhoneModal()}
      >
        <div className="relative w-full max-w-md mx-auto my-6">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            {/* 헤더 */}
            <div className="flex items-start justify-between p-4 border-b border-solid border-gray-300 rounded-t">
              <h3 className="text-xl font-semibold">
                별도 발송
              </h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black float-right text-2xl leading-none font-semibold outline-none focus:outline-none"
                onClick={handleCloseMultiPhoneModal}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            {/* 모달 본문 */}
            <div className="relative p-6 flex-auto">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">기준 발송번호</p>
                <p className="text-base border p-2 rounded bg-gray-50">{currentSmsRecipient}</p>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">추가 발송번호</p>
                  <button 
                    onClick={handleAddPhoneInput}
                    className="text-blue-600 text-sm flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    번호 추가
                  </button>
                </div>
                
                {multiPhoneInputs.map((phone, idx) => (
                  <div key={idx} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={phone}
                      onChange={(e) => handlePhoneInputChange(idx, e.target.value)}
                      placeholder="전화번호 입력"
                      className="border rounded p-2 w-full"
                      autoFocus={idx === multiPhoneInputs.length - 1}
                    />
                    {multiPhoneInputs.length > 1 && (
                      <button 
                        onClick={() => handleRemovePhoneInput(idx)}
                        className="ml-2 text-red-500"
                      >
                        <MinusIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="text-xs text-gray-500 mb-4">
                <p>※ 전화번호는 하이픈(-) 없이 숫자만 입력해도 됩니다.</p>
                <p>※ 입력한 모든 번호로 동일한 내용의 SMS가 발송됩니다.</p>
              </div>
            </div>
            
            {/* 푸터 */}
            <div className="flex items-center justify-end p-4 border-t border-solid border-gray-300 rounded-b">
              <button
                className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-2 mb-1"
                type="button"
                onClick={handleCloseMultiPhoneModal}
              >
                취소
              </button>
              <button
                className="bg-blue-600 text-white active:bg-blue-700 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mb-1"
                type="button"
                onClick={handleSendMultiPhoneSMS}
                disabled={isLoadingAction}
              >
                {isLoadingAction ? '발송 중...' : '문자 발송'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // 모달이 닫혀 있으면 렌더링하지 않음
  if (!isOpen) return null;

  // 모달 바깥 클릭 핸들러
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div>
      {/* 별도 발송 모달 */}
      <MultiPhoneModal />
      
      {/* 메인 모달 */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50"
        onClick={handleBackdropClick}
      >
        <div className="relative w-full max-w-[95vw] mx-auto my-6 max-h-[90vh] overflow-auto">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            {/* 모달 헤더 */}
            <div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
              <h3 className="text-2xl font-semibold">
                행사 SMS 상세 정보
              </h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          
          {/* 모달 본문 */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">로딩 중...</span>
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <p className="text-red-500">{error}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* 행사 정보 섹션 */}
                <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">행사 정보</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-sm font-medium text-gray-500">행사명</div>
                        <div className="col-span-2 text-sm text-gray-900">
                          {eventData.EV_NM || '-'}
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-sm font-medium text-gray-500">출발일</div>
                        <div className="col-span-2 text-sm text-gray-900">
                          {eventData.START_DAY ? 
                            `${eventData.START_DAY.substring(0, 4)}/${eventData.START_DAY.substring(4, 6)}/${eventData.START_DAY.substring(6, 8)}` : 
                            '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* SMS 발송 정보 섹션 */}
                <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-medium text-gray-900">SMS 발송 정보</h4>
                    <button
                      type="button"
                      id="send-all-button"
                      className={`px-4 py-2 text-sm font-medium text-white rounded-md ${selectedRows.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                      disabled={selectedRows.length === 0 || isLoadingAction}
                      onClick={handleBulkSendSMS}
                    >
                      일괄 발송
                    </button>
                  </div>
                  
                  <div className="bg-white rounded-md shadow-sm overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 w-12">
                            <input 
                              type="checkbox" 
                              className="h-4 w-4 text-blue-600 rounded"
                              checked={selectAll}
                              onChange={() => handleSelectAllRows()} 
                            />
                          </th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">대표자명</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">휴대폰 번호</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">예약처</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">왕편 일정</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">복편 일정</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">버스</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">미팅 일정</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">일정명</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">문자 발송</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500">발송 상태</th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500"></th>
                        </tr>
                        <tr>
                          <th colSpan="7"></th>
                          <th className="px-2 py-3 text-center">
                            <select 
                              className="w-full text-sm border-gray-300 rounded-md"
                              value={bulkMeetingTime}
                              onChange={(e) => {
                                setBulkMeetingTime(e.target.value);
                                handleBulkMeetingTimeChange(e.target.value);
                              }}
                            >
                              <option value="">일괄 미팅 시간 설정</option>
                              {Array.from({ length: 24 }).map((_, hour) => 
                                Array.from({ length: 6 }).map((_, minute) => {
                                  const time = `${hour.toString().padStart(2, '0')}:${(minute * 10).toString().padStart(2, '0')}`;
                                  return <option key={time} value={time}>{time}</option>;
                                })
                              )}
                            </select>
                          </th>
                          <th className="px-2 py-3 text-center">
                            <select 
                              className="w-full text-sm border-gray-300 rounded-md"
                              value={bulkScheduleName}
                              onChange={(e) => {
                                setBulkScheduleName(e.target.value);
                                handleBulkScheduleNameChange(e.target.value);
                              }}
                            >
                              <option value="">일괄 일정명 설정</option>
                              {dataList.length > 0 && dataList[0].nameList && 
                                dataList[0].nameList.map((name, idx) => (
                                  <option key={idx} value={name}>{name}</option>
                                ))
                              }
                            </select>
                          </th>
                          <th colSpan="3"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                          <tr>
                            <td colSpan="12" className="px-4 py-4 text-center text-gray-500">
                              데이터를 불러오는 중...
                            </td>
                          </tr>
                        ) : error ? (
                          <tr>
                            <td colSpan="12" className="px-4 py-4 text-center text-red-500">
                              {error}
                            </td>
                          </tr>
                        ) : dataList.length === 0 ? (
                          <tr>
                            <td colSpan="12" className="px-4 py-4 text-center text-gray-500">
                              표시할 데이터가 없습니다.
                            </td>
                          </tr>
                        ) : (
                          dataList.map((item, index) => {
                            // 전화번호 형식 생성
                            const phone = item.HANDPHONE1 + '-' + item.HANDPHONE2 + '-' + item.HANDPHONE3;
                            
                            // 발송 상태에 따른 클래스 및 텍스트 설정
                            let statusClass = '';
                            let statusText = '-';
                            const sendSmsStatus = eventDetail[index].status;

                            if (sendSmsStatus === 'Success') {
                              statusClass = 'text-blue-600 font-bold';
                              statusText = (
                                <a 
                                  href={`http://ktxtourticket.co.kr:8080/?reservationSeq=${item.RES_SEQ}&reservationDate=${item.RES_DAY}&userId=${item.RES_DAY}-${item.RES_SEQ}.${item.EV_YM}-${item.EV_SEQ}`} 
                                  target="_blank" 
                                  className="text-blue-600 font-bold hover:underline"
                                  rel="noreferrer"
                                >
                                  성공
                                </a>
                              );
                            } else if (sendSmsStatus === 'Fail') {
                              statusClass = 'text-red-600 font-bold';
                              statusText = '실패';
                            }
                            
                            return (
                              <tr key={index} id={`row-${index+1}`} className="hover:bg-gray-50">
                                {/* 체크박스 */}
                                <td className="px-2 py-3 text-center align-middle text-xs w-12">
                                  <input 
                                    type="checkbox" 
                                    className="h-4 w-4 text-blue-600 rounded"
                                    checked={selectedRows.includes(index)}
                                    onChange={() => handleRowSelection(index)} 
                                  />
                                </td>
                                
                                {/* 대표자명 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  {item.RES_NM || '-'}
                                </td>
                                
                                {/* 휴대폰 번호 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  {phone || '-'}
                                </td>
                                
                                {/* 예약처 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  {item.SITE_NM || '-'}
                                </td>
                                
                                {/* 왕편 일정 */}
                                <td className="px-1 py-2 text-center align-middle text-xs whitespace-nowrap">
                                  {item.outBoundContent ? (
                                    <>
                                      <div className="font-medium">{item.outBoundContent}</div>
                                      <div className="text-gray-500">{item.outbound}</div>
                                    </>
                                  ) : (
                                    <div className="text-gray-400">배정 전</div>
                                  )}
                                  <input
                                    type="text"
                                    className="mt-1 w-full text-xs border border-gray-200 rounded h-6 px-1 focus:border-blue-400 focus:outline-none"
                                    placeholder="중간역"
                                    value={eventDetail[index].out_bound_note || ''}
                                    onChange={(e) => {
                                      const newDataList = [...dataList];
                                      newDataList[index].outBoundNote = e.target.value;
                                      eventDetail[index].out_bound_note = e.target.value;
                                      setDataList(newDataList);
                                    }}
                                  />
                                </td>

                                {/* 복편 일정 */}
                                <td className="px-1 py-2 text-center align-middle text-xs whitespace-nowrap">
                                  {item.inBoundContent ? (
                                    <>
                                      <div className="font-medium">{item.inBoundContent}</div>
                                      <div className="text-gray-500">{item.inbound}</div>
                                    </>
                                  ) : (
                                    <div className="text-gray-400">배정 전</div>
                                  )}
                                  <input
                                    type="text"
                                    className="mt-1 w-full text-xs border border-gray-200 rounded h-6 px-1 focus:border-blue-400 focus:outline-none"
                                    placeholder="중간역"
                                    value={eventDetail[index].in_bound_note || ''}
                                    onChange={(e) => {
                                      const newDataList = [...dataList];
                                      newDataList[index].inBoundNote = e.target.value;
                                      eventDetail[index].in_bound_note = e.target.value;
                                      setDataList(newDataList);
                                    }}
                                  />
                                </td>

                                {/* 버스 배정 */}
                                {/* 버스 배정 */}
                                {(() => {
                                  if (!hasBusServices) {
                                    return (
                                      <td className="px-2 py-3 text-center align-middle text-sm text-gray-300">-</td>
                                    );
                                  }
                                  const userId = `${item.RES_DAY}-${item.RES_SEQ}.${eventData.EV_YM}-${eventData.EV_SEQ}`;
                                  const busSeats = busAssignmentMap[userId] || [];
                                  const isAssigned = busSeats.length > 0;
                                  const tooltipText = isAssigned
                                    ? busSeats.map(s => `${s.car_number}호차 ${s.seat_code}번`).join(', ')
                                    : '버스 미배정';
                                  return (
                                    <td className="px-2 py-3 text-center align-middle text-sm">
                                      <div className="relative group inline-block">
                                        {isAssigned ? (
                                          <CheckCircleSolidIcon className="h-5 w-5 text-green-500 mx-auto" />
                                        ) : (
                                          <XCircleIcon className="h-5 w-5 text-gray-300 mx-auto" />
                                        )}
                                        <div className="absolute z-10 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap">
                                          {tooltipText}
                                        </div>
                                      </div>
                                    </td>
                                  );
                                })()}

                                {/* 미팅 일정 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  <select
                                    className="w-full text-sm border-gray-300 rounded-md"
                                    value={eventDetail[index].meeting || '09:00'}
                                    onChange={(e) => {
                                      const newDataList = [...dataList];
                                      newDataList[index].meeting = e.target.value;
                                      eventDetail[index].meeting = e.target.value;
                                      setDataList(newDataList);
                                    }}
                                  >
                                    {Array.from({ length: 24 }).map((_, hour) => 
                                      Array.from({ length: 6 }).map((_, minute) => {
                                        const time = `${hour.toString().padStart(2, '0')}:${(minute * 10).toString().padStart(2, '0')}`;
                                        return <option key={time} value={time}>{time}</option>;
                                      })
                                    )}
                                  </select>
                                </td>
                                
                                {/* 일정명 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  <select 
                                    className="w-full text-sm border-gray-300 rounded-md"
                                    value={eventDetail[index].schedule_name || ''}
                                    onChange={(e) => {
                                      const newDataList = [...dataList];
                                      newDataList[index].schedule_name = e.target.value;
                                      eventDetail[index].schedule_name = e.target.value;
                                      setDataList(newDataList);
                                    }}
                                  >
                                    {item.nameList && item.nameList.map((name, idx) => (
                                      <option key={idx} value={name}>{name}</option>
                                    ))}
                                  </select>
                                </td>
                                
                                {/* 문자 발송 버튼 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  <button 
                                    type="button" 
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                                    onClick={() => handleSendSMS(index, phone)}
                                    disabled={isLoadingAction}
                                  >
                                    발송하기
                                  </button>
                                </td>
                                
                                {/* 발송 상태 */}
                                <td className={`px-2 py-3 text-center align-middle text-sm ${statusClass}`}>
                                  {statusText}
                                </td>
                                
                                {/* 별도 발송 버튼 */}
                                <td className="px-2 py-3 text-center align-middle text-sm">
                                  <button 
                                    type="button" 
                                    className="px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded-md hover:bg-gray-300 transition-colors"
                                    onClick={() => handleOpenMultiPhoneModal(index, item.RES_NM)}
                                  >
                                    별도 발송
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
            
              </div>
            )}
          </div>
          
          {/* 모달 푸터 */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SMSDetailModel;